import { CommonModule } from '@angular/common';
import { Component, input, output, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SliderModule } from 'primeng/slider';
import { FoxeraInputTextComponent } from '@foxera/shared/components/foxera-input-text/foxera-input-text.component';
import { FoxeraDrawerFullComponent } from '@foxera/shared/components/foxera-drawer-full/foxera-drawer-full.component';
import { ResponsiveService } from '@foxera/shared/services/responsive.service';

export type DialogType = 'duration' | 'title';

@Component({
  selector: 'app-foxera-edit-dialog',
  standalone: true,
  templateUrl: './foxera-edit-dialog.component.html',
  styleUrls: ['./foxera-edit-dialog.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    SliderModule,
    FoxeraInputTextComponent,
    FoxeraDrawerFullComponent
  ]
})
export class FoxeraEditDialogComponent {
  readonly responsiveService = inject(ResponsiveService);
  
  readonly type = input.required<DialogType>();
  readonly sectionTitle = input<string>('');
  readonly dayNumber = input<number>(0);
  readonly value = input<number | string>(0);
  
  readonly save = output<number | string>();
  readonly close = output<void>();

  visible = true;
  tempValue: number | string = 0;
  formSubmitted: boolean = false;
  titleForm!: FormGroup;

  constructor(private fb: FormBuilder) {
    this.initForm();
  }

  private initForm(): void {
    this.titleForm = this.fb.group({
      title: ['', [Validators.required]]
    });
  }

  ngOnChanges(): void {
    this.tempValue = this.value();
    this.formSubmitted = false;
    
    // Update form control for title type
    if (this.type() === 'title') {
      this.titleForm.patchValue({
        title: this.value().toString()
      });
      this.titleForm.markAsUntouched();
      this.titleForm.markAsPristine();
    }
  }

  onSave(): void {
    // For title type, trigger form validation
    if (this.type() === 'title') {
      this.formSubmitted = true;
      this.titleForm.markAllAsTouched();
      
      // Check if form is invalid
      if (this.titleForm.invalid) {
        return; // Don't save if invalid
      }
      
      // Get the value from form control
      this.tempValue = this.titleForm.get('title')?.value || '';
    }
    
    this.save.emit(this.tempValue);
  }

  onHide(): void {
    this.close.emit();
  }

  getDialogTitle(): string {
    return this.type() === 'duration' ? 'Edit Duration' : 'Edit Day Title';
  }

  getIconClass(): string {
    return this.type() === 'duration' ? 'pi-clock' : 'pi-pencil';
  }

  getDialogWidth(): string {
    return this.type() === 'duration' ? '400px' : '450px';
  }

  get inputConfig() {
    return {
      label: 'Day Title',
      placeholder: 'Enter day title...',
      type: 'text' as const,
      required: true,
      requiredErrorMessage: 'Day title is required'
    };
  }
}
